import type { AmplitudeIdentify } from '../types/amplitude.js';
import {
  AMPLITUDE_RETRY_MAX_ATTEMPTS,
  AMPLITUDE_RETRY_DELAY_MS,
} from '../config.js';
import { setTrackingField } from '../cookie/tracking-cookie.js';
import { hashEmail } from '../utils/hash.js';
import { logger } from '../utils/logger.js';

/**
 * Attend que window.amplitude soit disponible (chargé et initialisé par Webflow
 * via le snippet cdn.amplitude.com/script/{API_KEY}.js).
 * Appelle `onReady` quand window.amplitude est prêt.
 */
export function loadAmplitude(onReady: () => void, attempts: number = 0): void {
  if (window.amplitude) {
    onReady();
    return;
  }
  if (attempts < AMPLITUDE_RETRY_MAX_ATTEMPTS) {
    setTimeout(() => loadAmplitude(onReady, attempts + 1), AMPLITUDE_RETRY_DELAY_MS);
  } else {
    logger.warn('Amplitude SDK non chargé après', attempts, 'tentatives — vérifier Webflow');
  }
}

/**
 * Ne fait rien : Amplitude est auto-initialisé par le snippet Webflow.
 * Conservée pour compatibilité de l'orchestrateur.
 */
export function initAmplitude(): void {
  if (!window.amplitude) {
    logger.warn('initAmplitude appelé sans SDK chargé');
    return;
  }
  logger.info('Amplitude déjà initialisé par Webflow (snippet auto-init)');
}

/**
 * Exécute un identify Amplitude.
 */
export function amplitudeIdentify(identify: AmplitudeIdentify): void {
  if (!window.amplitude) {
    logger.warn('amplitudeIdentify appelé sans SDK chargé');
    return;
  }
  window.amplitude.identify(identify);
  logger.info('Amplitude identify envoyé');
}

/**
 * Récupère le device ID Amplitude et le sauvegarde dans le cookie tracking.
 * Retry avec backoff car getDeviceId() peut retourner undefined au début.
 */
export function saveAmplitudeDeviceId(attempts: number = 0): void {
  const deviceId = window.amplitude?.getDeviceId();
  if (deviceId) {
    setTrackingField('amplitude_device_id', deviceId);
    logger.info('amplitude_device_id sauvegardé:', deviceId);
    return;
  }
  if (attempts < AMPLITUDE_RETRY_MAX_ATTEMPTS) {
    setTimeout(() => saveAmplitudeDeviceId(attempts + 1), AMPLITUDE_RETRY_DELAY_MS);
  } else {
    logger.warn('Impossible de récupérer amplitude_device_id après', attempts, 'tentatives');
  }
}

/**
 * Pose le user_id Amplitude à `sha256(email)`. Stable cross-device.
 *
 * - Si email vide/invalide → ne fait rien (user reste anonyme).
 * - Si Amplitude pas encore chargé → log warning, no-op.
 * - Si le hash est identique au userId déjà posé → no-op (évite les appels redondants).
 *
 * Async car sha256 via Web Crypto API est async.
 */
export async function setAmplitudeUserId(email: string | undefined | null): Promise<void> {
  if (!email) {
    logger.info('setAmplitudeUserId: pas d\'email, skip');
    return;
  }
  if (!window.amplitude) {
    logger.warn('setAmplitudeUserId appelé sans SDK chargé');
    return;
  }
  const hash = await hashEmail(email);
  if (!hash) {
    logger.warn('setAmplitudeUserId: email invalide après normalisation');
    return;
  }
  // Optimisation : éviter les appels redondants si même hash
  const currentUserId = window.amplitude.getUserId?.();
  if (currentUserId === hash) {
    logger.info('setAmplitudeUserId: user_id déjà à jour, skip');
    return;
  }
  window.amplitude.setUserId(hash);
  logger.info('Amplitude user_id posé:', hash.substring(0, 8) + '…');
}
